import { useEffect, useState } from 'react';
import { isNotNullish } from '@chainflip/utils/guard';
import { abbreviate } from '@chainflip/utils/string';
import { useMutation } from '@tanstack/react-query';
import { BigNumber } from 'bignumber.js';
import { EmptyFrame } from '@/shared/assets/frames/EmptyFrame';
import { AddressInput as VanityNameInput } from '@/shared/components/AddressInput';
import Toggle from '@/shared/components/atoms/Toggle';
import {
  DashboardModalWithCloseButton,
  ModalContent,
  StatsPanel,
} from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { FundFlipModalContent } from '@/shared/components/PolkadotConnectionButton/FundFlipModal';
import useBoolean from '@/shared/hooks/useBoolean';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useToast from '@/shared/hooks/useToast';
import CheckCircle from '@/shared/icons/flip-ui-kit/large/CheckCircle';
import ForbidIcon from '@/shared/icons/flip-ui-kit/large/Forbid';
import DelegateIcon from '@/shared/icons/flip-ui-kit/small/Delegate';
import KeyIcon from '@/shared/icons/flip-ui-kit/small/Key';
import NetworkIcon from '@/shared/icons/flip-ui-kit/small/Network';
import PercentageIcon from '@/shared/icons/flip-ui-kit/small/Percentage';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/small/PlusCircle';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import type TokenAmount from '@/shared/utils/TokenAmount';
import useSettingsModal from '../../../hooks/useSettingsModal';
import { useExtrinsicSDK, useStateChainAccount } from '../../../hooks/useStateChainAccount';

const FormItems = ({
  toggleDelegationEnabled,
  setDelegationFee,
  vanityName,
  setVanityName,
  ...props
}: {
  delegationEnabled: boolean;
  toggleDelegationEnabled: () => void;
  delegationFee: number;
  setDelegationFee: (fee: number) => void;
  vanityName: string;
  setVanityName: (vanityName: string) => void;
}) => {
  const [feePct, setFeePct] = useState<string | null>(
    new BigNumber(props.delegationFee).div(100).toFixed(2),
  );

  useEffect(() => {
    if (feePct) setDelegationFee(new BigNumber(feePct).times(100).toNumber());
  }, [feePct]);

  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex justify-between">
        <span className="flex items-center gap-x-1 text-14 text-cf-light-3">Enable Delegation</span>
        <Toggle onToggle={toggleDelegationEnabled} value={props.delegationEnabled} />
      </div>
      <div className="relative flex items-center justify-between text-14 text-cf-light-3">
        <span className="flex items-center gap-x-1">Delegation Commission</span>
        <input
          className="w-[64px] max-w-fit rounded-md border border-cf-gray-4 bg-cf-gray-3-5 px-2 text-cf-light-3 outline-none"
          type="text"
          value={feePct ?? ''}
          onChange={(e) => {
            if (e.target.value === '') {
              setFeePct(null);
              return;
            }
            const newValue = new BigNumber(e.target.value);
            if (newValue.isNaN() || newValue.lt(0)) return;
            setFeePct(e.target.value);
          }}
          onBlur={() => {
            setFeePct(new BigNumber(feePct ?? '0').toFixed(2));
          }}
        />
        <PercentageIcon className="absolute right-1 h-[16px] w-[16px]" />
      </div>
      {setVanityName && (
        <VanityNameInput
          fullWidth
          placeholder="Vanity Name"
          address={vanityName}
          setAddress={setVanityName}
          allowFlexibleInput
        />
      )}
    </div>
  );
};

const DelegationConfirmationPanel = ({
  delegationEnabled,
  vanityName,
  flipBalance,
  operatorAddress,
}: {
  delegationEnabled: boolean;
  vanityName: string | undefined;
  flipBalance: TokenAmount;
  operatorAddress: string | undefined;
}) => (
  <StatsPanel
    className="text-14"
    items={[
      {
        label: 'Name',
        Icon: NetworkIcon,
        content: vanityName ? (
          <div className="flex items-center gap-x-1 text-cf-white">
            <span>{vanityName}</span>
            <CopyButton textToCopy={vanityName} variant="ghost" />
          </div>
        ) : (
          'Operator'
        ),
      },
      {
        label: 'Operator public key (SS58)',
        Icon: KeyIcon,
        content: operatorAddress ? (
          <div className="flex items-center gap-x-1 text-cf-white">
            <span>{abbreviate(operatorAddress, 4)}</span>
            <CopyButton textToCopy={operatorAddress} variant="ghost" />
          </div>
        ) : (
          'N/A'
        ),
      },
      {
        label: 'Delegation',
        Icon: DelegateIcon,
        content: <span className="text-cf-white">{delegationEnabled ? 'On' : 'Off'}</span>,
      },
      {
        label: 'Funds added',
        Icon: PlusCircleIcon,
        content: (
          <span className="text-cf-white">
            {flipBalance.toFormatted()} {FLIP_SYMBOL}
          </span>
        ),
      },
    ]}
  />
);

const SuccessView = ({
  onClose,
  delegationEnabled,
  vanityName,
  operatorAddress,
  flipBalance,
}: {
  onClose: () => void;
  delegationEnabled: boolean;
  vanityName: string | undefined;
  operatorAddress: string | undefined;
  flipBalance: TokenAmount;
}) => (
  <div className="flex w-full flex-col items-center">
    <div className="relative h-28">
      <div className="absolute left-1/2 h-[120px] -translate-x-1/2">
        <EmptyFrame />
      </div>
      <div className="absolute left-1/2 top-[60px] -translate-x-1/2">
        <CheckCircle className="scale-150 text-cf-green-1" />
      </div>
    </div>
    <div className="flex w-full flex-col items-stretch gap-y-6">
      <div className="flex flex-col gap-y-2 text-center font-aeonikMedium">
        <span className="text-24 text-cf-white">Operator registered</span>
        <span className="text-14 text-cf-light-2">
          You&apos;re now able to participate in auctions
        </span>
      </div>

      <DelegationConfirmationPanel
        delegationEnabled={delegationEnabled}
        vanityName={vanityName}
        flipBalance={flipBalance}
        operatorAddress={operatorAddress}
      />

      <Button fullWidth onClick={onClose}>
        Done
      </Button>
    </div>
  </div>
);

const RegisterView = ({
  flipBalance,
  delegationFee,
  vanityName,
  setVanityName,
  delegationEnabled,
  onComplete,
  ...props
}: {
  flipBalance: TokenAmount;
  delegationEnabled: boolean;
  delegationFee: number;
  vanityName: string;
  setVanityName: (vanityName: string) => void;
  toggleDelegationEnabled: () => void;
  setDelegationFee: (fee: number) => void;
  onComplete: () => void;
}) => {
  const { sendExtrinsic, toast } = useExtrinsicSDK('registerAsOperator');

  const { mutate: registerAsOperator, isPending } = useMutation({
    mutationFn: async () => {
      toast.loading('Submitting transaction to the State Chain...');
      return sendExtrinsic(delegationFee, delegationEnabled ? 'Allow' : 'Deny', vanityName);
    },
    onSuccess() {
      toast.success('Successfully registered as Operator');
      onComplete();
    },
    onError(err) {
      toast.error('Error while submitting transaction:', {
        description: err.message,
      });
    },
  });

  return (
    <ModalContent
      title="Register as Operator"
      description={
        <>
          Before you start, you need to register your{' '}
          <span className="text-white">State Chain</span> account as an Operator
        </>
      }
      buttons={
        <Button
          fullWidth
          onClick={() => registerAsOperator()}
          loading={isPending && 'iconOnly'}
          disabled={flipBalance.eq(0) || 'ifLoading'}
        >
          Confirm Registration
        </Button>
      }
      buttonDivider
    >
      <FormItems
        delegationEnabled={delegationEnabled}
        delegationFee={delegationFee}
        vanityName={vanityName}
        setVanityName={setVanityName}
        {...props}
      />
    </ModalContent>
  );
};

export default function SettingsModal() {
  const { shown, hide } = useSettingsModal();
  const { selectedAccount } = usePolkadot();
  const { flipBalance, account, accountInfo } = useStateChainAccount();
  const toast = useToast('waiting-toast');
  const [waitingForBalance, setWaitingForBalance] = useState(false);

  const {
    value: delegationEnabled,
    toggle: toggleDelegationEnabled,
    setValue: setDelegationEnabled,
  } = useBoolean(account?.upcoming_delegation.delegation_acceptance === 'Allow');
  const [delegationFee, setDelegationFee] = useState(
    account?.upcoming_delegation.delegation_fee_bps ?? 1500,
  );

  const [vanityName, setVanityName] = useState(accountInfo?.vanity_name ?? '');
  const {
    value: successViewShown,
    setTrue: showSuccessView,
    setFalse: hideSuccessView,
  } = useBoolean(false);

  useEffect(() => {
    if (!shown) return;
    if (account) {
      setDelegationFee(account.upcoming_delegation.delegation_fee_bps);
      setDelegationEnabled(account.upcoming_delegation.delegation_acceptance === 'Allow');
    }
    if (accountInfo?.vanity_name) {
      setVanityName(accountInfo.vanity_name);
    }
  }, [
    account?.upcoming_delegation.delegation_fee_bps,
    account?.upcoming_delegation.delegation_acceptance,
    shown,
    accountInfo?.vanity_name,
  ]);

  const reset = () => {
    setVanityName('');
    hideSuccessView();
  };

  const { sendExtrinsic: deregister, toast: deregisterToast } = useExtrinsicSDK(
    'operator_deregisterAsOperator',
  );
  const { sendExtrinsic: signAndSendBatch, toast: batchToast } =
    useExtrinsicSDK('signAndSendBatch');

  const shouldUpdateVanityName = vanityName !== accountInfo?.vanity_name;

  const newSetting = delegationEnabled ? 'Allow' : 'Deny';
  const shouldUpdateSettings =
    isNotNullish(account) &&
    (account.upcoming_delegation.delegation_acceptance !== newSetting ||
      account.upcoming_delegation.delegation_fee_bps !== delegationFee);

  const { mutate: updateOperatorSettings, isPending: settingsUpdatePending } = useMutation({
    mutationFn: async () => {
      if (!shouldUpdateSettings && !shouldUpdateVanityName) return;

      batchToast.loading('Updating Operator settings...');
      await signAndSendBatch((sdk) => {
        if (shouldUpdateSettings) sdk.operator_updateOperatorSettings(delegationFee, newSetting);
        if (shouldUpdateVanityName) sdk.accountRoles_setVanityName(vanityName);
      });
    },
    onSuccess() {
      batchToast.success('Successfully updated Operator settings');
      hide();
    },
    onError(error) {
      batchToast.error('Error updating Operator settings:', {
        description: error.message,
      });
    },
  });

  const { mutate: deregisterAsOperator, isPending: deregisterPending } = useMutation({
    mutationFn: async () => {
      deregisterToast.loading('Submitting transaction to the State Chain...');
      return deregister();
    },
    onSuccess() {
      deregisterToast.success('Successfully deregistered as Operator');
      hide();
    },
    onError(err) {
      deregisterToast.error('Error while submitting transaction:', {
        description: err.message,
      });
    },
  });

  useEffect(() => {
    if (!flipBalance) return;

    if (!shown) {
      toast.dismiss();
      return;
    }

    if (waitingForBalance && flipBalance.eq(0)) {
      toast.loading('Waiting for account to be funded...', {
        description: `Please be patient while your ${FLIP_SYMBOL} is credited to your State Chain account.`,
      });
    } else if (flipBalance.gt(0)) {
      toast.dismiss();
      setWaitingForBalance(false);
    }
  }, [waitingForBalance, shown, flipBalance]);

  // TODO(delegation): add loading state?
  if (!flipBalance) return null;

  let content;

  if (successViewShown) {
    content = (
      <SuccessView
        onClose={hide}
        delegationEnabled={delegationEnabled}
        flipBalance={flipBalance}
        vanityName={vanityName}
        operatorAddress={selectedAccount?.idSs58}
      />
    );
  } else if (accountInfo?.role === 'unregistered') {
    if (flipBalance.eq(0) && !waitingForBalance) {
      content = (
        <FundFlipModalContent
          open={shown}
          onClose={hide}
          type="operator"
          onFunded={() => {
            setWaitingForBalance(true);
          }}
        />
      );
    } else {
      content = (
        <RegisterView
          delegationEnabled={delegationEnabled}
          delegationFee={delegationFee}
          flipBalance={flipBalance}
          toggleDelegationEnabled={toggleDelegationEnabled}
          setDelegationFee={setDelegationFee}
          onComplete={showSuccessView}
          vanityName={vanityName}
          setVanityName={setVanityName}
        />
      );
    }
  } else {
    content = (
      <ModalContent
        title="Operator Settings"
        buttons={
          <>
            <Button
              icon={
                <div>
                  <ForbidIcon className="mr-1 text-cf-red-2" />
                </div>
              }
              iconPos="left"
              type="secondary-standard"
              onClick={() => deregisterAsOperator()}
              loading={deregisterPending}
            >
              Deregister
            </Button>
            <Button
              fullWidth
              type="primary-standard"
              loading={settingsUpdatePending}
              onClick={() => updateOperatorSettings()}
            >
              Save changes
            </Button>
          </>
        }
        buttonDivider
      >
        <FormItems
          delegationEnabled={delegationEnabled}
          delegationFee={delegationFee}
          setDelegationFee={setDelegationFee}
          toggleDelegationEnabled={toggleDelegationEnabled}
          vanityName={vanityName}
          setVanityName={setVanityName}
        />
      </ModalContent>
    );
  }

  return (
    <DashboardModalWithCloseButton open={shown} onClose={hide} afterLeave={reset}>
      {content}
    </DashboardModalWithCloseButton>
  );
}
